import * as vp from 'vopay';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BankVerifyHistory.module.scss';
import { Button } from 'UI';

const { Box, Select, TextField, DateRangePicker, AVAILABLE_TIMEZONES } = vp;

type BankVerifySearchParams = {
  requestStatus: string;
  verificationStatus: string;
  searchTerm: string;
  timeRange: [vp.Timestamp, vp.Timestamp];
  timeZone: string;
};

type SearchFormProps = {
  requestStatus: string;
  timeZone: vp.Timezone;
  timeRange?: [vp.Timestamp, vp.Timestamp];
  verificationStatus: string;
  searchTerm: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  hasInvalidEndDate?: boolean;
  shouldShowEndDateError?: boolean;
  setShouldShowEndDateError?: (event: boolean) => void;
  search: (searchParams: BankVerifySearchParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const BankVerifyHistoryForm: FC = (props: SearchFormProps) => {
  const {
    requestStatus,
    timeZone,
    verificationStatus,
    searchTerm,
    initialTimeRange,
    hasInvalidEndDate,
    shouldShowEndDateError,
    setShouldShowEndDateError,
    search,
  } = props;
  const [currentRequestStatus, setCurrentRequestStatus] = useState<string>(requestStatus);
  const [currentVerificationStatus, setCurrentVerificationStatus] = useState<string>(verificationStatus);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>(searchTerm);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const { t } = useTranslation('translations', { keyPrefix: 'BankVerifyHistory.SearchForm' });

  const requestStatusOptions = [
    { value: 'all', label: t('Options.RequestStatusOptions.all') },
    { value: 'active', label: t('Options.RequestStatusOptions.active') },
    { value: 'expired', label: t('Options.RequestStatusOptions.expired') },
  ];

  const verificationStatusOptions = [
    { value: 'all', label: t('Options.VerificationStatusOptions.all') },
    { value: 'verified', label: t('Options.VerificationStatusOptions.verified') },
    { value: 'unverified', label: t('Options.VerificationStatusOptions.unverified') },
    { value: 'failed', label: t('Options.VerificationStatusOptions.failed') },
  ];

  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.formContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
          hasInvalidEndDate={hasInvalidEndDate}
          shouldShowEndDateError={shouldShowEndDateError}
          setShouldShowEndDateError={setShouldShowEndDateError}
        />
        <Select
          version="2"
          placeholder={t('requestStatus')}
          options={requestStatusOptions}
          name="status"
          value={currentRequestStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentRequestStatus(e.target.value);
          }}
        />
        <Select
          version="2"
          placeholder={t('verificationStatus')}
          options={verificationStatusOptions}
          name="verificationStatus"
          value={currentVerificationStatus}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentVerificationStatus(e.target.value);
          }}
        />
        <Select
          version="2"
          placeholder={t('timezone')}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value);
          }}
        />
        <TextField
          key="AccountSearchKeyword"
          name="AccountSearchKeyword"
          placeholder={t('keyword')}
          value={currentSearchTerm}
          onBlur={() => {}}
          onChange={(e) => {
            setCurrentSearchTerm(e.target.value);
          }}
          variant="outlineShadow"
        />
        <Button
          id="search-verify"
          size="small"
          colorScheme="primary"
          onClick={() => {
            search({
              requestStatus: currentRequestStatus,
              verificationStatus: currentVerificationStatus,
              searchTerm: currentSearchTerm,
              timeRange,
              timeZone: currentTimeZone,
            });
          }}
        >
          {t('search')}
        </Button>
      </div>
    </Box>
  );
};

export default BankVerifyHistoryForm;
